/** The Help menu (DESIGN UD-18, P8): guide, glossary, shortcuts, limitations, model card, copy diagnostics. */
import { signal } from '@preact/signals';
import { useEffect } from 'preact/hooks';
import { diagnosticsSections, diagnosticsText } from '../../shared/diagnostics.ts';
import { hasFigures, modelCardHrefFor } from '../../shared/model-figures.ts';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { hashFor, navigate } from '../router.ts';
import { wallMs } from '../state.ts';
import { HELP } from '../strings-help.ts';
import { TOOLBAR } from '../strings-reader.ts';
import { Dialog, MenuButton } from '../ui.tsx';

const shortcutsOpen = signal(false);
const copyNote = signal('');

/** Open the shortcuts dialog (the reader's own button uses it). */
export function openShortcuts(): void {
	shortcutsOpen.value = true;
}

/** `TOOLBAR.shortcutsHelp` ("j and k: paragraphs · n and N: evidence · …") as key and action pairs. */
export function shortcutRows(text: string): Array<[key: string, action: string]> {
	return text.split(' · ').map((part) => {
		const colon = part.indexOf(': ');
		if (colon >= 0) return [part.slice(0, colon), part.slice(colon + 2)];
		const space = part.indexOf(' ');
		return space >= 0 ? [part.slice(0, space), part.slice(space + 1)] : [part, ''];
	});
}

const SHORTCUT_ROWS: Array<[string, string]> = [
	...shortcutRows(TOOLBAR.shortcutsHelp),
	[HELP.shortcutsExtraKey, HELP.shortcutsExtraAction],
];

function typing(t: EventTarget | null): boolean {
	const el = t as HTMLElement | null;
	if (!el?.tagName) return false;
	return (
		el.tagName === 'INPUT' ||
		el.tagName === 'TEXTAREA' ||
		el.tagName === 'SELECT' ||
		el.isContentEditable
	);
}

function open(href: string): void {
	window.open(href, '_blank', 'noopener');
}

async function copyDiagnostics(vm: ViewModel): Promise<void> {
	try {
		const sections = diagnosticsSections(vm, { wallMs: wallMs.value ?? undefined });
		await navigator.clipboard.writeText(diagnosticsText(sections));
		copyNote.value = HELP.diagnosticsCopied;
	} catch {
		copyNote.value = HELP.diagnosticsCopyFailed;
	}
	window.setTimeout(() => {
		copyNote.value = '';
	}, 2500);
}

export function HelpMenu({ vm }: { vm: ViewModel }) {
	// the card of the model that produced this result
	const modelCardHref = hasFigures(vm.ensemble.model.id)
		? modelCardHrefFor(vm.ensemble.model.id)
		: HELP.modelCardHref;
	// `?` opens the shortcuts while a result is shown, unless the user is typing
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== '?' || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
			if (typing(e.target)) return;
			e.preventDefault();
			shortcutsOpen.value = true;
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, []);
	return (
		<>
			<MenuButton
				label={HELP.menu}
				items={[
					{
						id: 'how-to-read',
						label: HELP.howToRead,
						onSelect: () => navigate(hashFor('glossary')),
					},
					{ id: 'glossary', label: HELP.glossary, onSelect: () => navigate(hashFor('glossary')) },
					{
						id: 'shortcuts',
						label: HELP.shortcuts,
						onSelect: () => {
							shortcutsOpen.value = true;
						},
					},
					{ id: 'user-guide', label: HELP.userGuide, onSelect: () => open(HELP.userGuideHref) },
					{
						id: 'limitations',
						label: HELP.limitations,
						onSelect: () => open(HELP.limitationsHref),
					},
					{ id: 'model-card', label: HELP.modelCard, onSelect: () => open(modelCardHref) },
					{
						id: 'copy-diagnostics',
						label: HELP.copyDiagnostics,
						onSelect: () => void copyDiagnostics(vm),
					},
				]}
			/>
			<output class="visually-hidden">{copyNote.value}</output>
			<Dialog
				title={HELP.shortcutsTitle}
				open={shortcutsOpen.value}
				onClose={() => {
					shortcutsOpen.value = false;
				}}
				actions={
					<button
						type="button"
						class="btn btn-primary"
						onClick={() => {
							shortcutsOpen.value = false;
						}}
					>
						{HELP.close}
					</button>
				}
			>
				<dl class="shortcut-list">
					{SHORTCUT_ROWS.flatMap(([key, action]) => [
						<dt key={`k-${key}`}>{key}</dt>,
						<dd key={`a-${key}`}>{action}</dd>,
					])}
				</dl>
			</Dialog>
		</>
	);
}
